import { useCallback, useEffect, useRef } from 'react';
import invariant from 'invariant';
import { useDataActions } from './useDataActions';
import { useClearable } from './useClearable';
import { useRegvarValidator } from './useRegvarValidator';
import { useIsFormReadOnly } from './useIsFormReadOnly';
import { useMountEffect } from './useMountEffect';
export const useRegvar = (rv, allProps)=>{
    invariant(rv, 'rv is missing.');
    invariant(allProps, 'props is missing.');
    const isFormReadOnly = useIsFormReadOnly();
    const dataActions = useDataActions();
    const regvarRef = useRef(rv);
    const propsRef = useRef(allProps);
    const val = useRegvarValidator();
    regvarRef.current = rv;
    propsRef.current = allProps;
    const skipValidation = isFormReadOnly || rv.$rcc.regvarMetadata.serversideOnly;
    useClearable((row, options)=>row.setRegvarValue(regvarRef.current.$rcc.shortname, null, options));
    // Every time the regvar's props changes, check if the validation state has changed.
    useEffect(()=>{
        if (skipValidation) {
            return;
        }
        const regvar = regvarRef.current;
        const props = propsRef.current;
        const { disableValidation } = props;
        const value = regvar.value;
        const shortname = regvar.$rcc.shortname;
        let errors = [];
        if (regvar.include && !disableValidation) {
            errors = val.validate(value, props, regvar.$rcc.regvarMetadata);
        }
        if (regvar.errorsOutOfSync(errors)) {
            dataActions.updateRow((row)=>row.setRegvarErrors(shortname, errors), {
                skipHistory: true
            });
        }
    });
    useMountEffect(undefined, ()=>{
        if (skipValidation) {
            return;
        }
        const regvar = regvarRef.current;
        const shortname = regvar.$rcc.shortname;
        dataActions.updateRow((row)=>row.setRegvarErrors(shortname, []), {
            skipHistory: true
        });
    });
    const setValue = useCallback((value, options)=>{
        const regvar = regvarRef.current;
        const currentRawValue = regvar.value;
        if (value !== currentRawValue || !regvar.$isDirty) {
            const shortname = regvar.$rcc.shortname;
            const errors = skipValidation || !regvar.include ? [] : val.validate(value, propsRef.current, regvar.$rcc.regvarMetadata);
            dataActions.updateRow((row)=>row.setRegvarValue(shortname, value, options || {
                    markAsDirty: true
                }).setRegvarErrors(shortname, errors), {
                skipHistory: value === currentRawValue
            });
        }
    }, [
        regvarRef,
        propsRef,
        dataActions,
        skipValidation,
        val
    ]);
    const setInclude = useCallback((include, options)=>{
        const regvar = regvarRef.current;
        const currentRawValue = regvar.include;
        if (include !== currentRawValue) {
            const shortname = regvar.$rcc.shortname;
            const errors = skipValidation || !regvar.include ? [] : val.validate(regvar.value, propsRef.current, regvar.$rcc.regvarMetadata);
            dataActions.updateRow((row)=>row.setRegvarInclude(shortname, include, options || {
                    markAsDirty: true
                }).setRegvarErrors(shortname, errors));
        }
    }, [
        regvarRef,
        propsRef,
        dataActions,
        skipValidation,
        val
    ]);
    const setDirty = useCallback((value)=>{
        const regvar = regvarRef.current;
        if (regvar.$isDirty !== value) {
            const shortname = regvar.$rcc.shortname;
            dataActions.updateRow((row)=>row.setIn([
                    'regvars',
                    shortname,
                    '$isDirty'
                ], value), {
                skipHistory: true
            });
        }
    }, [
        regvarRef,
        dataActions
    ]);
    return [
        setValue,
        setInclude,
        setDirty
    ];
};
